/**
 * Campaigns Zustand Store
 *
 * Manages all state for Platform Campaigns including:
 * - Campaign list with filters and pagination
 * - Active campaign details
 * - Campaign CRUD operations
 * - Enrollment management
 * - Campaign stats
 * - Builder wizard state
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  PlatformCampaign,
  PlatformCampaignListFilters,
  PaginationMeta,
  CreatePlatformCampaignDto,
  UpdatePlatformCampaignDto,
  CampaignEnrollment,
  CampaignStats,
  PlatformCampaignStatus,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface CampaignsError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface CampaignsState {
  // Campaigns List
  campaigns: PlatformCampaign[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: CampaignsError | null;

  // Filters
  filters: PlatformCampaignListFilters;

  // Active Campaign
  activeCampaignId: string | null;
  activeCampaign: PlatformCampaign | null;
  isLoadingCampaign: boolean;
  campaignError: CampaignsError | null;

  // Campaign Stats
  campaignStats: CampaignStats | null;
  isLoadingStats: boolean;

  // Enrollments
  enrollments: CampaignEnrollment[];
  enrollmentsPagination: PaginationMeta | null;
  isLoadingEnrollments: boolean;

  // Builder Wizard State
  wizardStep: number; // 0: Info, 1: Trigger, 2: Audience, 3: Steps, 4: Review
  draftCampaign: Partial<CreatePlatformCampaignDto>;

  // Action States
  isPerformingAction: boolean;
  actionError: CampaignsError | null;

  // Actions - List
  loadCampaigns: (page?: number) => Promise<void>;
  refreshCampaigns: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof PlatformCampaignListFilters>(key: K, value: PlatformCampaignListFilters[K]) => void;
  clearFilters: () => void;

  // Actions - Active Campaign
  loadCampaign: (id: string) => Promise<void>;
  selectCampaign: (id: string) => void;
  clearActiveCampaign: () => void;

  // Actions - Stats & Enrollments
  loadStats: (campaignId: string) => Promise<void>;
  loadEnrollments: (campaignId: string, page?: number) => Promise<void>;

  // Actions - CRUD
  createCampaign: (dto: CreatePlatformCampaignDto) => Promise<string | null>;
  updateCampaign: (id: string, dto: UpdatePlatformCampaignDto) => Promise<boolean>;
  deleteCampaign: (id: string) => Promise<boolean>;
  activateCampaign: (id: string) => Promise<boolean>;
  pauseCampaign: (id: string) => Promise<boolean>;

  // Actions - Wizard
  setWizardStep: (step: number) => void;
  updateDraftCampaign: (data: Partial<CreatePlatformCampaignDto>) => void;
  clearDraftCampaign: () => void;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Campaigns List
  campaigns: [] as PlatformCampaign[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as CampaignsError | null,

  // Filters
  filters: {} as PlatformCampaignListFilters,

  // Active Campaign
  activeCampaignId: null as string | null,
  activeCampaign: null as PlatformCampaign | null,
  isLoadingCampaign: false,
  campaignError: null as CampaignsError | null,

  // Campaign Stats
  campaignStats: null as CampaignStats | null,
  isLoadingStats: false,

  // Enrollments
  enrollments: [] as CampaignEnrollment[],
  enrollmentsPagination: null as PaginationMeta | null,
  isLoadingEnrollments: false,

  // Builder Wizard State
  wizardStep: 0,
  draftCampaign: {} as Partial<CreatePlatformCampaignDto>,

  // Action States
  isPerformingAction: false,
  actionError: null as CampaignsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useCampaignsStore = create<CampaignsState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadCampaigns: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.trigger) params.set('trigger', filters.trigger);

      const response = await api.get<{ data: PlatformCampaign[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.LIST}?${params.toString()}`
      );

      set({
        campaigns: response.data,
        pagination: response.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_CAMPAIGNS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load campaigns',
        },
      });
    }
  },

  refreshCampaigns: async () => {
    const { pagination } = get();
    await get().loadCampaigns(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadCampaigns(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadCampaigns(1);
  },

  // ============================================
  // Active Campaign Actions
  // ============================================

  loadCampaign: async (id: string) => {
    set({ activeCampaignId: id, isLoadingCampaign: true, campaignError: null });
    try {
      const api = getApiClient();
      const response = await api.get<PlatformCampaign>(
        API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.CAMPAIGN(id)
      );

      set({ activeCampaign: response, isLoadingCampaign: false });
    } catch (error) {
      set({
        isLoadingCampaign: false,
        campaignError: {
          code: 'LOAD_CAMPAIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load campaign',
        },
      });
    }
  },

  selectCampaign: (id: string) => {
    const { campaigns } = get();
    const campaign = campaigns.find((c) => c.id === id);
    if (campaign) {
      set({ activeCampaignId: id, activeCampaign: campaign });
    } else {
      get().loadCampaign(id);
    }
  },

  clearActiveCampaign: () => {
    set({
      activeCampaignId: null,
      activeCampaign: null,
      campaignStats: null,
      enrollments: [],
      enrollmentsPagination: null,
      campaignError: null,
    });
  },

  // ============================================
  // Stats & Enrollments Actions
  // ============================================

  loadStats: async (campaignId: string) => {
    set({ isLoadingStats: true });
    try {
      const api = getApiClient();
      const response = await api.get<CampaignStats>(
        API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.STATS(campaignId)
      );

      set({ campaignStats: response, isLoadingStats: false });
    } catch {
      set({ isLoadingStats: false });
    }
  },

  loadEnrollments: async (campaignId: string, page = 1) => {
    set({ isLoadingEnrollments: true });
    try {
      const api = getApiClient();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      params.set('campaignId', campaignId);

      const response = await api.get<{ data: CampaignEnrollment[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.ENROLLMENTS}?${params.toString()}`
      );

      set({
        enrollments: response.data,
        enrollmentsPagination: response.pagination,
        isLoadingEnrollments: false,
      });
    } catch {
      set({ isLoadingEnrollments: false });
    }
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createCampaign: async (dto: CreatePlatformCampaignDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<PlatformCampaign>(
        API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.CREATE, dto
      );

      // Refresh the list and clear draft
      await get().refreshCampaigns();
      set({ isPerformingAction: false, draftCampaign: {}, wizardStep: 0 });
      return response.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_CAMPAIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create campaign',
        },
      });
      return null;
    }
  },

  updateCampaign: async (id: string, dto: UpdatePlatformCampaignDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<PlatformCampaign>(
        API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.UPDATE(id), dto
      );

      // Update local state
      set((state) => ({
        campaigns: state.campaigns.map((c) =>
          c.id === id ? { ...c, ...response } : c
        ),
        activeCampaign:
          state.activeCampaign?.id === id
            ? { ...state.activeCampaign, ...response }
            : state.activeCampaign,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_CAMPAIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update campaign',
        },
      });
      return false;
    }
  },

  deleteCampaign: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.DELETE(id));

      // Remove from local state
      set((state) => ({
        campaigns: state.campaigns.filter((c) => c.id !== id),
        activeCampaign: state.activeCampaign?.id === id ? null : state.activeCampaign,
        activeCampaignId: state.activeCampaignId === id ? null : state.activeCampaignId,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_CAMPAIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete campaign',
        },
      });
      return false;
    }
  },

  activateCampaign: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post<PlatformCampaign>(API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.ACTIVATE(id), {});

      // Update local state
      set((state) => ({
        campaigns: state.campaigns.map((c) =>
          c.id === id ? { ...c, status: 'active' as const } : c
        ),
        activeCampaign:
          state.activeCampaign?.id === id
            ? { ...state.activeCampaign, status: 'active' as const }
            : state.activeCampaign,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'ACTIVATE_CAMPAIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to activate campaign',
        },
      });
      return false;
    }
  },

  pauseCampaign: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post<PlatformCampaign>(API_ENDPOINTS.SUPER_ADMIN.CAMPAIGNS.PAUSE(id), {});

      // Update local state
      set((state) => ({
        campaigns: state.campaigns.map((c) =>
          c.id === id ? { ...c, status: 'paused' as const } : c
        ),
        activeCampaign:
          state.activeCampaign?.id === id
            ? { ...state.activeCampaign, status: 'paused' as const }
            : state.activeCampaign,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'PAUSE_CAMPAIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to pause campaign',
        },
      });
      return false;
    }
  },

  // ============================================
  // Wizard Actions
  // ============================================

  setWizardStep: (step: number) => {
    set({ wizardStep: step });
  },

  updateDraftCampaign: (data: Partial<CreatePlatformCampaignDto>) => {
    set((state) => ({
      draftCampaign: { ...state.draftCampaign, ...data },
    }));
  },

  clearDraftCampaign: () => {
    set({ draftCampaign: {}, wizardStep: 0 });
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, campaignError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get campaigns by status */
export const useCampaignsByStatus = (status: PlatformCampaignStatus) => {
  return useCampaignsStore((state) =>
    state.campaigns.filter((c) => c.status === status)
  );
};

/** Get active campaigns count */
export const useActiveCampaignsCount = () => {
  return useCampaignsStore((state) =>
    state.campaigns.filter((c) => c.status === 'active').length
  );
};

/** Get total enrollment count across campaigns */
export const useTotalEnrollmentCount = () => {
  return useCampaignsStore((state) =>
    state.campaigns.reduce((sum, c) => sum + c.enrolledCount, 0)
  );
};

/** Check if any loading state is active */
export const useCampaignsLoading = () => {
  const isLoadingList = useCampaignsStore((state) => state.isLoadingList);
  const isLoadingCampaign = useCampaignsStore((state) => state.isLoadingCampaign);
  const isPerformingAction = useCampaignsStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingCampaign || isPerformingAction;
};

/** Get campaign by ID */
export const useCampaignById = (id: string) => {
  return useCampaignsStore((state) =>
    state.campaigns.find((c) => c.id === id)
  );
};

/** Get draft campaign for wizard */
export const useDraftCampaign = () => {
  return useCampaignsStore((state) => state.draftCampaign);
};

/** Get current wizard step */
export const useWizardStep = () => {
  return useCampaignsStore((state) => state.wizardStep);
};

/** Get active filters count */
export const useCampaignFiltersCount = () => {
  return useCampaignsStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.status) count++;
    if (state.filters.trigger) count++;
    return count;
  });
};
